import { useEffect, useState } from 'react';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import {
  fetchAppSharingState,
  setAppSharing,
} from '@/lib/appSharing';
import type { MarketplaceVendorRow } from '@/lib/marketplaceCatalog';

interface AppShareModalProps {
  appvendeurId: number;
  appName: string;
  appIcon: string;
  commerceId: number;
  vendors: MarketplaceVendorRow[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Modal de partage d'une application aux vendeurs de la marketplace.
 * Partage global (tous les vendeurs) ou sélection nominative.
 */
export default function AppShareModal({
  appvendeurId,
  appName,
  appIcon,
  commerceId,
  vendors,
  onClose,
  onSaved,
}: AppShareModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shared, setShared] = useState(false);
  const [all, setAll] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState('0');
  const [condition, setCondition] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      const state = await fetchAppSharingState(appvendeurId);
      if (!active) return;
      setShared(state.shared);
      setAll(state.all);
      setSelected(new Set(state.vendorIds));
      setLimit(String(state.limiteVendeur || 0));
      setCondition(state.conditionPlus || '');
      setLoading(false);
    })();
    return () => { active = false; };
  }, [appvendeurId]);

  const toggleVendor = (userId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await setAppSharing({
        appvendeurId,
        marketplaceId: commerceId,
        shared,
        all,
        vendorIds: [...selected],
        limiteVendeur: Number(limit) || 0,
        conditionPlus: condition,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filteredVendors = q
    ? vendors.filter((v) => (v.nomcommerce || '').toLowerCase().includes(q) || (v.ville || '').toLowerCase().includes(q))
    : vendors;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
              <i className={`${appIcon} text-lg`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold font-heading text-foreground-950 truncate">Partager l&apos;application</h3>
              <p className="text-xs text-foreground-500 truncate">{appName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 flex-shrink-0"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-14">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : (
          <>
            {/* Toggle partage */}
            <button
              type="button"
              onClick={() => setShared((v) => !v)}
              className={`w-full flex items-center gap-3 p-4 rounded-lg border transition-colors cursor-pointer ${
                shared ? 'bg-accent-50 border-accent-200/70' : 'bg-background-100 border-background-200/70'
              }`}
            >
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${shared ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-500'}`}>
                <i className={shared ? 'ri-share-forward-fill' : 'ri-share-forward-line'}></i>
              </span>
              <span className="flex-1 text-left min-w-0">
                <span className="block text-sm font-semibold text-foreground-900">
                  {shared ? 'Partagée avec mes vendeurs' : 'Non partagée'}
                </span>
                <span className="block text-xs text-foreground-500 mt-0.5">
                  Vos vendeurs pourront utiliser cette app dans leur espace.
                </span>
              </span>
              <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${shared ? 'bg-accent-500' : 'bg-background-300'}`}>
                <span className={`inline-block transform rounded-full bg-white transition-transform ${shared ? 'translate-x-6' : 'translate-x-1'}`} style={{ width: 18, height: 18 }} />
              </span>
            </button>

            {shared && (
              <div className="mt-4">
                {vendors.length === 0 ? (
                  <div className="px-4 py-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                    <i className="ri-store-2-line text-2xl text-foreground-300"></i>
                    <p className="mt-2 text-sm text-foreground-500">
                      Aucun vendeur rattaché à votre marketplace pour le moment.
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">
                      Le partage s&apos;appliquera automatiquement dès qu&apos;un vendeur rejoindra.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Mode de partage */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setAll(true)}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                          all ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                        }`}
                      >
                        <i className="ri-group-line"></i>
                        Tous les vendeurs
                      </button>
                      <button
                        type="button"
                        onClick={() => setAll(false)}
                        className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                          !all ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                        }`}
                      >
                        <i className="ri-user-follow-line"></i>
                        Vendeurs sélectionnés
                      </button>
                    </div>

                    {!all && (
                      <div className="rounded-lg border border-background-200/70 overflow-hidden">
                        <div className="relative border-b border-background-200/70">
                          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                          <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Rechercher un vendeur…"
                            className="w-full pl-9 pr-3 py-2.5 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                          />
                        </div>
                        <div className="max-h-64 overflow-y-auto divide-y divide-background-200/70">
                          {filteredVendors.length === 0 ? (
                            <p className="px-4 py-6 text-center text-xs text-foreground-400">Aucun vendeur trouvé</p>
                          ) : (
                            filteredVendors.map((v) => {
                              const img = resolveMediaUrl(v.image);
                              const on = selected.has(v.userId);
                              return (
                                <button
                                  key={v.userId}
                                  type="button"
                                  onClick={() => toggleVendor(v.userId)}
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors cursor-pointer ${
                                    on ? 'bg-accent-50' : 'hover:bg-background-100'
                                  }`}
                                >
                                  <span className="w-8 h-8 rounded-full bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                                    {img ? (
                                      <img src={img} alt={v.nomcommerce} className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-xs font-bold text-foreground-400">{v.nomcommerce.charAt(0).toUpperCase()}</span>
                                    )}
                                  </span>
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-medium text-foreground-900 truncate">{v.nomcommerce}</span>
                                    <span className="block text-[11px] text-foreground-400 truncate">{v.ville || 'Ville non précisée'}</span>
                                  </span>
                                  <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border ${
                                    on ? 'bg-accent-500 border-accent-500 text-background-50' : 'border-background-300 text-transparent'
                                  }`}>
                                    <i className="ri-check-line text-xs"></i>
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                        <div className="px-3 py-2 bg-background-100 text-[11px] text-foreground-500">
                          {selected.size} vendeur{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {shared && (
              <div className="mt-4 rounded-lg border border-background-200/70 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-speed-up-line text-primary-500"></i>
                  <h4 className="text-sm font-semibold text-foreground-900">Limite par vendeur</h4>
                </div>
                <p className="text-[11px] text-foreground-400 mb-3">
                  Ex. limitez à 1 calendrier (ZCalendar) par vendeur. Laissez 0 pour autoriser sans limite.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Limite (0 = illimité)</label>
                    <input
                      type="number"
                      min={0}
                      value={limit}
                      onChange={(e) => setLimit(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Condition pour en avoir plus</label>
                    <input
                      type="text"
                      value={condition}
                      onChange={(e) => setCondition(e.target.value)}
                      placeholder="Ex. contacter la marketplace pour une offre Pro"
                      className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 px-3.5 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 pt-5">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
                ) : (
                  <><i className="ri-save-line"></i>Enregistrer</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}